import Clarity from '@microsoft/clarity';
import * as Sentry from '@sentry/react';
import dayjs from 'dayjs';
import 'dayjs/locale/pl';
import relativeTime from 'dayjs/plugin/relativeTime';
import weekday from 'dayjs/plugin/weekday';
import 'modules/auth/amplify-config';
import { lazy, StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { I18nextProvider } from 'react-i18next';
import { appConfig } from 'shared/appConfig';
import 'styles/index.css';
import App from './App';
import { GraphqlProvider } from './gql/GraphqlProvider';
import i18n from './locale/i18n';
import { RealtimerProvider } from './realtimer/RealtimerProvider';

if (appConfig.sentryDsn) {
  Sentry.init({
    dsn: appConfig.sentryDsn,
    sendDefaultPii: true,
  });
}

if (appConfig.clarityProjectId) {
  Clarity.init(appConfig.clarityProjectId);
}

dayjs.extend(relativeTime);
dayjs.locale('pl');
dayjs.extend(weekday);

const ServiceUnavailablePage = lazy(() => import('./pages/ServiceUnavailablePage'));

const isTestingModeEnabled = localStorage.getItem('testingModeEnabled') === 'true';
const isServiceUnavailable = import.meta.env.VITE_API_IS_SERVICE_UNAVAILABLE === 'true';

const displayServiceUnavailablePage = isServiceUnavailable && !isTestingModeEnabled;

window.addEventListener('vite:preloadError', () => {
  window.location.reload();
});

createRoot(document.getElementById('root')!, {
  onUncaughtError: Sentry.reactErrorHandler((error, errorInfo) =>
    console.error('Uncaught error', error, errorInfo.componentStack),
  ),
  onCaughtError: Sentry.reactErrorHandler(),
  onRecoverableError: Sentry.reactErrorHandler(),
}).render(
  <StrictMode>
    <I18nextProvider i18n={i18n}>
      <GraphqlProvider>
        <RealtimerProvider>
          {displayServiceUnavailablePage ? <ServiceUnavailablePage /> : <App />}
        </RealtimerProvider>
      </GraphqlProvider>
    </I18nextProvider>
  </StrictMode>,
);
